import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/Auth";
import { WorkspaceProvider } from "./WorkspaceContext";
import {
  deriveWorkspacesFromWorkbooks,
  deriveWorkspacesFromTables,
  mergeWorkspaceLists,
  normalizeCode,
} from "./deriveWorkspaces";
import WorkspaceCommandPalette from "./WorkspaceCommandPalette";
import { BackgroundGradientAnimation } from "./BackgroundGradientAnimation";
import {
  buildWorkspaceAdminPath,
  buildWorkspaceDashboardPath,
  normalizeWorkspaceGroupForPath,
} from "./workspaceRoutes";

export default function WorkspaceStart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const didOpenPalette = useRef(false);

  const available = useMemo(() => {
    const wsFromBooks = deriveWorkspacesFromWorkbooks(user);
    const wsFromTables = deriveWorkspacesFromTables(user);
    return mergeWorkspaceLists(wsFromBooks, wsFromTables);
  }, [user]);

  const getDefaultRouteForWorkspace = (code: string | null, group?: string) => {
    const normalized = normalizeCode(code ?? "");
    const requestedGroup = normalizeWorkspaceGroupForPath(group);

    if (user?.workbooks) {
      for (const [groupName, byWorkspace] of Object.entries(user.workbooks)) {
        if (normalizeWorkspaceGroupForPath(groupName) !== requestedGroup)
          continue;
        for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
          if (normalizeCode(workspaceName) !== normalized) continue;
          for (const entries of Object.values(byMenu)) {
            for (const uuid of Object.keys(entries)) {
              return buildWorkspaceDashboardPath({
                group: requestedGroup,
                workspace: workspaceName,
                uuid,
              });
            }
          }
        }
      }
    }

    if (user?.tables) {
      for (const [groupName, byMenu] of Object.entries(user.tables)) {
        if (normalizeWorkspaceGroupForPath(groupName) !== requestedGroup)
          continue;
        for (const [sectionName, tables] of Object.entries(byMenu)) {
          if (normalizeCode(sectionName) !== normalized) continue;
          const routeClient =
            user?.section_route_overrides?.[sectionName] ??
            sectionName.toLowerCase();
          const firstTable = Object.keys(tables)[0];
          if (firstTable)
            return buildWorkspaceAdminPath({
              group: requestedGroup,
              workspace: sectionName,
              path: `${routeClient}/${firstTable}`,
            });
        }
      }
    }

    return "/workspace-start";
  };

  useEffect(() => {
    if (available.length === 0) {
      navigate("/auth/error?reason=no_access", { replace: true });
      return;
    }

    if (available.length <= 1) {
      const first = available[0];
      if (first?.code) {
        navigate(getDefaultRouteForWorkspace(first.code), {
          replace: true,
        });
      } else {
        navigate("/workspace-start", { replace: true });
      }
      return;
    }

    if (didOpenPalette.current) return;
    didOpenPalette.current = true;
    window.dispatchEvent(new Event("open-command-palette"));
  }, [available, navigate]);

  return (
    <WorkspaceProvider initialAvailable={available}>
      <BackgroundGradientAnimation
        gradientBackgroundStart="rgb(248, 250, 252)"
        gradientBackgroundEnd="rgb(255, 255, 255)"
        firstColor="14, 165, 233"
        secondColor="59, 130, 246"
        thirdColor="236, 72, 153"
        fourthColor="99, 102, 241"
        fifthColor="16, 185, 129"
        pointerColor="59, 130, 246"
        size="90%"
      />
      <WorkspaceCommandPalette
        onWorkspaceSelected={(ws, options) => {
          const route = getDefaultRouteForWorkspace(ws.code ?? null, ws.group);
          if (options?.openInNewTab) {
            window.open(route, "_blank", "noopener,noreferrer");
            return;
          }
          navigate(route, { replace: true });
        }}
      />
    </WorkspaceProvider>
  );
}
